import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Button, Toast } from 'antd-mobile';
import { CloseOutline, CloseCircleFill, DownOutline, RightOutline } from 'antd-mobile-icons';
import { Header } from '../components/Header';
import { ScanInput } from '../components/ScanInput';
import { Pda, useScanResult } from '../components/Pda';
import { t } from '../../../i18n';
import {
    BAG_STATUS_DISCARDED,
    BAG_STATUS_HANDOVER,
} from '../../../graphql/queries/bagQueries';
import { parseException } from '../../../utils/newExceptionUtils';
import {
    GET_BAG_MERGE_SCAN_INFO_BY_NUMBER,
    GetBagMergeScanInfoByNumberResponse,
    BagMergeScanInfoType,
} from './graphql/getBagMergeScanInfo';
import {
    HANDLE_BAG_MERGE_EVENT,
    HandleBagMergeEventResponse,
} from './graphql/handleBagMergeEvent';

/** Bag status: 0-Unused 1-Used 2-Discarded 3-Handover 4-Closed */
const BAG_STATUS_USED = 1;
/** BusinessType: 2-TT_SORT */
const BUSINESS_TYPE_TT = 2;
/** PrintType: 1-SwiftX */
const PRINT_TYPE_SWIFTX = 1;
/** 单次合包总包裹数上限，对齐后端 BagMergeApplication.MAX_ORDER_COUNT */
const MAX_TOTAL_ORDER_COUNT = 3000;

/** 左列分拣展示：面单为 SWX 时按 sortingCode；否则 TT 用 sortingRule，其他类型用 sortingCode（对齐 BaggingScanPage） */
const getSortingDisplay = (bag: BagMergeScanInfoType): string => {
    const isSwxPrint = bag.printType === PRINT_TYPE_SWIFTX;
    const isTtBag =
        bag.businessType === BUSINESS_TYPE_TT ||
        bag.bagNumber.toUpperCase().startsWith('TT');
    if (!isSwxPrint && isTtBag) {
        return bag.sortingRule ?? '';
    }
    return bag.sortingCode ?? '';
};

let currentBagMergeErrorToast: any = null;

const showBagMergeError = (message: string) => {
    if (currentBagMergeErrorToast) {
        currentBagMergeErrorToast.close();
        currentBagMergeErrorToast = null;
    }
    currentBagMergeErrorToast = Toast.show({
        content: (
            <div className="relative py-2" style={{ minWidth: '200px', maxWidth: '80vw' }}>
                <div
                    className="absolute -top-2 right-0 cursor-pointer flex items-center justify-center w-6 h-6"
                    onClick={() => {
                        if (currentBagMergeErrorToast) {
                            currentBagMergeErrorToast.close();
                            currentBagMergeErrorToast = null;
                        }
                    }}
                >
                    <span className="text-white text-lg">✕</span>
                </div>
                <div className="flex items-center justify-center mb-2">
                    <div className="flex items-center gap-2">
                        <CloseCircleFill fontSize={20} color='#c7381e' />
                        <div className="text-base">{t('pda.common.error')}</div>
                    </div>
                </div>
                <div className="text-sm text-center px-4" style={{ whiteSpace: 'pre-line' }}>{message}</div>
            </div>
        ),
        duration: 2000,
    });
};

type Step = 1 | 2;
type Tab = 'success' | 'failure';

type SuccessRecord = {
    id: number;
    targetBagNumber: string;
    targetOrderCount: number;
    sourceBags: { bagNumber: string; orderCount: number }[];
    time: string;
};

type FailureRecord = {
    id: number;
    targetBagNumber: string;
    attemptedBags: { bagNumber: string; orderCount: number }[];
    errorMessage: string;
    time: string;
};

const formatTime = (date: Date): string => {
    const hh = date.getHours();
    const mm = date.getMinutes().toString().padStart(2, '0');
    const period = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    return `${h12}:${mm} ${period}`;
};

const PageBody = () => {
    const [fetchBagMergeScanInfo] = useLazyQuery<GetBagMergeScanInfoByNumberResponse>(
        GET_BAG_MERGE_SCAN_INFO_BY_NUMBER,
        { fetchPolicy: 'network-only' }
    );
    const [handleBagMergeEvent] = useMutation<HandleBagMergeEventResponse>(
        HANDLE_BAG_MERGE_EVENT
    );

    const [step, setStep] = useState<Step>(1);
    const [scannedBags, setScannedBags] = useState<BagMergeScanInfoType[]>([]);
    const [targetBagId, setTargetBagId] = useState<number | null>(null);
    const [bagNumber, setBagNumber] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>('success');
    const [successRecords, setSuccessRecords] = useState<SuccessRecord[]>([]);
    const [failureRecords, setFailureRecords] = useState<FailureRecord[]>([]);
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [showSuccessFrame, setShowSuccessFrame] = useState(false);

    const scanResult = useScanResult();
    // 串行化 step1 扫描：异步 fetch 期间拒绝新扫描，避免连扫读到陈旧 scannedBags 绕过去重/限额校验
    const scanningRef = useRef(false);
    const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (scanResult) {
            handleScan(scanResult);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanResult]);

    useEffect(() => {
        return () => {
            if (successTimerRef.current) {
                clearTimeout(successTimerRef.current);
            }
        };
    }, []);

    const triggerSuccessFrame = () => {
        if (successTimerRef.current) {
            clearTimeout(successTimerRef.current);
        }
        setShowSuccessFrame(true);
        successTimerRef.current = setTimeout(() => {
            setShowSuccessFrame(false);
            successTimerRef.current = null;
        }, 2000);
    };

    const showError = (msg: string) => {
        showBagMergeError(msg);
    };

    const bagStatusText = (status: number): string => {
        switch (status) {
            case 0: return t('enum.bagStatus.notUsed');
            case BAG_STATUS_USED: return t('enum.bagStatus.used');
            case BAG_STATUS_DISCARDED: return t('enum.bagStatus.discarded');
            case BAG_STATUS_HANDOVER: return t('enum.bagStatus.handover');
            case 4: return t('enum.bagStatus.closed');
            default: return String(status);
        }
    };

    const handleScan = async (value: string) => {
        // 合包确认弹框打开 / mutation 提交中时屏蔽扫描，避免误扫改变 scannedBags / targetBag
        if (confirmOpen || submitting) return;
        const trimmed = value.trim();
        if (!trimmed) return;
        setBagNumber('');

        if (step === 1) {
            await handleStep1Scan(trimmed);
        } else {
            handleStep2Scan(trimmed);
        }
    };

    const handleStep1Scan = async (scannedBagNumber: string) => {
        // 上一次扫描仍在处理（fetch 未返回）时拒绝新扫描，避免并发读到陈旧 scannedBags
        if (scanningRef.current) {
            showError(t('pda.pages.bagMerge.scanInProgress'));
            return;
        }
        scanningRef.current = true;
        try {
            // 重复扫描检测
            if (scannedBags.find(b => b.bagNumber === scannedBagNumber)) {
                showError(t('pda.pages.bagMerge.duplicateBag'));
                return;
            }

            // 查询包牌合包扫描信息
            const { data, error } = await fetchBagMergeScanInfo({
                variables: { bagNumber: scannedBagNumber },
            });
            if (error || !data?.getBagMergeScanInfoByNumber) {
                showError(t('pda.pages.bagMerge.bagNotFound'));
                return;
            }
            const bag = data.getBagMergeScanInfoByNumber;

            // 实时校验（对齐 BagMergeApplication 后端校验顺序，前端早 fail）
            if (bag.status !== BAG_STATUS_USED) {
                showError(t('pda.pages.bagMerge.statusNotUsed', { status: bagStatusText(bag.status) }));
                return;
            }
            if ((bag.parcelCount ?? 0) === 0) {
                showError(t('pda.pages.bagMerge.emptyBag'));
                return;
            }
            if ((bag.mergeableParcelCount ?? 0) === 0) {
                showError(t('pda.pages.bagMerge.bagAllFinished'));
                return;
            }
            if (bag.businessType == null) {
                showError(t('pda.pages.bagMerge.businessTypeMissing'));
                return;
            }
            if (scannedBags.length > 0) {
                const first = scannedBags[0];
                if (bag.businessType !== first.businessType) {
                    showError(t('pda.pages.bagMerge.businessTypeMismatch'));
                    return;
                }
                if (getSortingDisplay(bag) !== getSortingDisplay(first)) {
                    showError(t('pda.pages.bagMerge.sortingCodeMismatch'));
                    return;
                }
            }

            const currentTotal = scannedBags.reduce((sum, b) => sum + (b.parcelCount ?? 0), 0);
            const projectedTotal = currentTotal + (bag.parcelCount ?? 0);
            if (projectedTotal > MAX_TOTAL_ORDER_COUNT) {
                showError(t('pda.pages.bagMerge.totalExceedLimit', {
                    totalCount: projectedTotal,
                    limit: MAX_TOTAL_ORDER_COUNT,
                }));
                return;
            }

            setScannedBags(prev => [...prev, bag]);
        } finally {
            scanningRef.current = false;
        }
    };

    const handleStep2Scan = (scannedBagNumber: string) => {
        const existing = scannedBags.find(b => b.bagNumber === scannedBagNumber);
        if (!existing) {
            showError(t('pda.pages.bagMerge.targetNotInSource'));
            return;
        }
        setTargetBagId(Number(existing.id));
    };

    const handleRemoveBag = (id: string) => {
        setScannedBags(prev => prev.filter(b => b.id !== id));
    };

    const handleClear = () => {
        setScannedBags([]);
        setTargetBagId(null);
    };

    const handleNext = () => {
        if (scannedBags.length < 2) {
            showError(t('pda.pages.bagMerge.insufficientBags'));
            return;
        }
        setStep(2);
        setTargetBagId(null);
    };

    const handleBack = () => {
        setStep(1);
        setTargetBagId(null);
    };

    const handleMergeClick = () => {
        if (!targetBagId) return;
        setConfirmOpen(true);
    };

    const handleConfirmCancel = () => {
        setConfirmOpen(false);
    };

    const handleConfirmOk = async () => {
        setConfirmOpen(false);
        await submitMerge();
    };

    const targetBag = scannedBags.find(b => Number(b.id) === targetBagId) ?? null;

    const resetScanState = () => {
        setStep(1);
        setScannedBags([]);
        setTargetBagId(null);
    };

    const toggleExpand = (id: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const submitMerge = async () => {
        if (!targetBagId) return;
        const attemptedTargetBag = scannedBags.find(b => Number(b.id) === targetBagId);
        const attemptedTargetBagNumber = attemptedTargetBag?.bagNumber ?? '';
        const attemptedBags = scannedBags.map(b => ({
            bagNumber: b.bagNumber,
            orderCount: b.parcelCount ?? 0,
        }));
        try {
            setSubmitting(true);
            const { data } = await handleBagMergeEvent({
                variables: {
                    input: {
                        sourceBagIds: scannedBags.map(b => Number(b.id)),
                        targetBagId,
                    },
                },
            });
            if (data?.handleBagMergeEvent) {
                const r = data.handleBagMergeEvent;
                const record: SuccessRecord = {
                    id: Date.now(),
                    targetBagNumber: r.targetBagNumber,
                    targetOrderCount: r.bagBreakdown.find(b => b.isTarget)?.orderCount ?? 0,
                    sourceBags: r.bagBreakdown
                        .filter(b => !b.isTarget)
                        .map(b => ({ bagNumber: b.bagNumber, orderCount: b.orderCount })),
                    time: formatTime(new Date()),
                };
                setSuccessRecords(prev => [record, ...prev]);
                setActiveTab('success');
                triggerSuccessFrame();
                resetScanState();
            }
        } catch (e: any) {
            // 全局 errorLink 仍会弹 toast；这里额外把错误捕获写入失败 Tab
            const { message } = parseException(e);
            const record: FailureRecord = {
                id: Date.now(),
                targetBagNumber: attemptedTargetBagNumber,
                attemptedBags,
                errorMessage: message,
                time: formatTime(new Date()),
            };
            setFailureRecords(prev => [record, ...prev]);
            setActiveTab('failure');
            resetScanState();
        } finally {
            setSubmitting(false);
        }
    };

    const renderSuccessRecord = (rec: SuccessRecord) => {
        const expanded = expandedIds.has(rec.id);
        return (
            <div key={rec.id} className="py-2 border-b border-gray-100">
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => toggleExpand(rec.id)}
                >
                    <span className="flex-shrink-0 w-4 text-gray-500">
                        {expanded ? <DownOutline /> : <RightOutline />}
                    </span>
                    <span className="flex-1 break-all text-sm font-medium">{rec.targetBagNumber}</span>
                    <span className="flex-shrink-0 w-14 text-right text-sm">{rec.targetOrderCount}</span>
                    <span className="flex-shrink-0 text-sm text-gray-500 w-16 text-right">{rec.time}</span>
                </div>
                {expanded && rec.sourceBags.length > 0 && (
                    <div className="pl-2 mt-1 space-y-1 border-l border-gray-200 ml-2">
                        {rec.sourceBags.map(b => (
                            <div key={b.bagNumber} className="flex items-center gap-2 text-sm text-gray-400 pl-2">
                                <span className="flex-1 min-w-0 truncate">{b.bagNumber}</span>
                                <span className="flex-shrink-0 w-14 text-right">{b.orderCount}</span>
                                <span className="flex-shrink-0 w-16" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderFailureRecord = (rec: FailureRecord) => {
        const expanded = expandedIds.has(rec.id);
        const totalAttempted = rec.attemptedBags.reduce((sum, b) => sum + b.orderCount, 0);
        return (
            <div key={rec.id} className="py-2 border-b border-gray-100">
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => toggleExpand(rec.id)}
                >
                    <span className="flex-shrink-0 w-4 text-gray-500">
                        {expanded ? <DownOutline /> : <RightOutline />}
                    </span>
                    <span className="flex-1 break-all text-sm font-medium">{rec.targetBagNumber}</span>
                    <span className="flex-shrink-0 w-14 text-right text-sm">{totalAttempted}</span>
                    <span className="flex-shrink-0 text-sm text-gray-500 w-16 text-right">{rec.time}</span>
                </div>
                {expanded && (
                    <div className="pl-2 mt-1 space-y-1 border-l border-gray-200 ml-2">
                        {rec.attemptedBags.map(b => (
                            <div key={b.bagNumber} className="flex items-center gap-2 text-sm text-gray-400 pl-2">
                                <span className="flex-1 min-w-0 truncate">{b.bagNumber}</span>
                                <span className="flex-shrink-0 w-14 text-right">{b.orderCount}</span>
                                <span className="flex-shrink-0 w-16" />
                            </div>
                        ))}
                        <div className="pl-2 text-sm text-red-500 break-all">
                            {t('pda.pages.bagMerge.failureReason')}: {rec.errorMessage}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const records = activeTab === 'success' ? successRecords : failureRecords;

    return (
        <div className="px-1 py-3 pb-24">
            {showSuccessFrame && (
                <div className="fixed inset-0 border-8 border-green-500 pointer-events-none transition-all duration-300 z-50 bg-green-50 bg-opacity-20"></div>
            )}
            <ScanInput
                placeholder={
                    step === 1
                        ? t('pda.pages.bagMerge.placeholderStep1')
                        : t('pda.pages.bagMerge.placeholderStep2')
                }
                value={bagNumber}
                onScan={handleScan}
                onChange={setBagNumber}
            />

            <div
                className={`mt-3 mb-3 bg-blue-50 rounded-lg px-1 ${
                    scannedBags.length === 0 ? 'py-2' : 'pt-1.5 pb-3'
                }`}
            >
                <div
                    className={`text-center text-xs text-gray-900 ${
                        scannedBags.length === 0 ? '' : 'mb-3'
                    }`}
                >
                    {step === 1
                        ? t('pda.pages.bagMerge.scannedCount', { count: scannedBags.length })
                        : t('pda.pages.bagMerge.step2ListLabel')}
                </div>
                {scannedBags.length > 0 && (
                    <div className="space-y-3">
                        {scannedBags.map(bag => {
                            const isTarget = step === 2 && Number(bag.id) === targetBagId;
                            return (
                                <div
                                    key={bag.id}
                                    className={`flex items-center gap-1.5 text-xs ${
                                        isTarget ? 'text-green-600' : 'text-gray-900'
                                    }`}
                                >
                                    <span className="flex-shrink-0">{getSortingDisplay(bag)}</span>
                                    <span className="flex-1 min-w-0 truncate text-center" title={bag.bagNumber}>{bag.bagNumber}</span>
                                    <span className="flex-shrink-0 text-right">{bag.parcelCount ?? 0}</span>
                                    {isTarget && (
                                        <span className="flex-shrink-0 text-green-600">
                                            [{t('pda.pages.bagMerge.kept')}]
                                        </span>
                                    )}
                                    {step === 1 ? (
                                        <CloseOutline
                                            className="flex-shrink-0 text-base text-gray-400 cursor-pointer"
                                            onClick={() => handleRemoveBag(bag.id)}
                                        />
                                    ) : (
                                        !isTarget && <span className="flex-shrink-0 w-4" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="mt-2">
                <div className="flex border-b border-gray-200">
                    <button
                        type="button"
                        className={`flex-1 py-2 text-base ${
                            activeTab === 'success'
                                ? 'text-purple-600 border-b-2 border-purple-600 font-bold'
                                : 'text-gray-500'
                        }`}
                        onClick={() => setActiveTab('success')}
                    >
                        {t('pda.pages.bagMerge.successTab')}({successRecords.length})
                    </button>
                    <button
                        type="button"
                        className={`flex-1 py-2 text-base ${
                            activeTab === 'failure'
                                ? 'text-purple-600 border-b-2 border-purple-600 font-bold'
                                : 'text-gray-500'
                        }`}
                        onClick={() => setActiveTab('failure')}
                    >
                        {t('pda.pages.bagMerge.failureTab')}({failureRecords.length})
                    </button>
                </div>
                <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500 border-b border-gray-100">
                    <span className="w-4" />
                    <span className="flex-1">{t('pda.pages.bagMerge.bagNoColumn')}</span>
                    <span className="flex-shrink-0 w-14 text-right">{t('pda.pages.bagMerge.quantityColumn')}</span>
                    <span className="flex-shrink-0 w-16 text-right">{t('pda.pages.bagMerge.timeColumn')}</span>
                </div>
                {records.length === 0 ? (
                    <div className="text-center text-gray-400 py-6 text-sm">
                        {t('pda.pages.bagMerge.noRecords')}
                    </div>
                ) : (
                    <div>
                        {activeTab === 'success'
                            ? successRecords.map(renderSuccessRecord)
                            : failureRecords.map(renderFailureRecord)}
                    </div>
                )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t flex gap-3">
                {step === 1 ? (
                    <>
                        <Button block onClick={handleClear} disabled={scannedBags.length === 0}>
                            {t('pda.pages.bagMerge.clear')}
                        </Button>
                        <Button
                            block
                            color="primary"
                            onClick={handleNext}
                            disabled={scannedBags.length < 2}
                        >
                            {t('pda.pages.bagMerge.nextStep')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button block onClick={handleBack}>
                            {t('pda.pages.bagMerge.back')}
                        </Button>
                        <Button
                            block
                            color="primary"
                            onClick={handleMergeClick}
                            disabled={!targetBagId}
                            loading={submitting}
                        >
                            {t('pda.pages.bagMerge.merge')}
                        </Button>
                    </>
                )}
            </div>

            {confirmOpen && targetBag && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-6">
                    <div className="bg-neutral-900 text-white rounded-xl w-full max-w-xs overflow-hidden">
                        <div className="relative flex items-center justify-center pt-4 pb-2">
                            <div className="flex items-center gap-2">
                                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                                    <path d="M12 2 L22 21 H2 Z" fill="#FFC107" />
                                    <rect x="11" y="9" width="2" height="7" fill="#1f1f1f" />
                                    <rect x="11" y="17" width="2" height="2" fill="#1f1f1f" />
                                </svg>
                                <span className="text-lg font-medium">
                                    {t('pda.pages.bagMerge.confirmTitle')}
                                </span>
                            </div>
                            <CloseOutline
                                className="absolute right-3 top-3 text-xl cursor-pointer text-white"
                                onClick={handleConfirmCancel}
                            />
                        </div>
                        <div className="px-4 pt-2 pb-2 text-center text-base">
                            {t('pda.pages.bagMerge.confirmDesc')}
                        </div>
                        <div className="px-4 pb-5 text-center text-2xl font-bold break-all">
                            {targetBag.bagNumber}
                        </div>
                        <div className="grid grid-cols-2 border-t border-neutral-700 text-base">
                            <button
                                className="py-3 border-r border-neutral-700 text-white"
                                onClick={handleConfirmCancel}
                            >
                                {t('pda.pages.bagMerge.cancel')}
                            </button>
                            <button
                                className="py-3 text-white"
                                onClick={handleConfirmOk}
                            >
                                {t('pda.pages.bagMerge.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default function BagMergeScanPage() {
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.bagMerge.title')} />
            <Pda />
            <PageBody />
        </div>
    );
}
